import React from 'react'
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Paper,
  Chip,
  Box,
  Typography,
  Skeleton,
  Autocomplete,
  TextField,
} from '@mui/material'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { modes } from '../../../Radiology/Constants/constants'
import {
  optionValue,
  LmstableHeading,
  externalName,
} from '../../../labTests/models'
import { makeStyles } from '../../../../utils/hooks/makeStyles'

const useStyles = makeStyles(() => ({
  tableContainer: {
    maxHeight: 'calc(100vh - 300px)',
    overflow: 'auto',
    boxShadow: '0px 3px 6px rgba(0,0,0,0.16)',
    borderRadius: 8,
  },
  headerCell: {
    backgroundColor: '#4682b4',
    color: 'white',
    fontWeight: 600,
    minWidth: 120,
  },
  headerCellWide: {
    backgroundColor: '#4682b4',
    color: 'white',
    fontWeight: 600,
    minWidth: 200,
  },
  headerCellExtraWide: {
    backgroundColor: '#4682b4',
    color: 'white',
    fontWeight: 600,
    minWidth: 250,
  },
  headerCellSmall: {
    backgroundColor: '#4682b4',
    color: 'white',
    fontWeight: 600,
    minWidth: 100,
  },
  headerCellAction: {
    backgroundColor: '#4682b4',
    color: 'white',
    fontWeight: 600,
    width: 100,
  },
  newRow: {
    backgroundColor: '#f5f5f5',
    '&:hover': {
      backgroundColor: '#e3f2fd',
    },
  },
  existingRow: {
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
    transition: 'background-color 0.2s',
  },
  externalInput: {
    marginTop: 8,
    padding: '8px 12px',
    width: '100%',
    border: '1px solid #ccc',
    borderRadius: 4,
  },
  priceChip: {
    fontWeight: 600,
  },
  addButton: {
    backgroundColor: '#4caf50',
    color: 'white',
    '&:hover': {
      backgroundColor: '#45a049',
    },
  },
  deleteButton: {
    '&:hover': {
      backgroundColor: '#ffebee',
    },
  },
  emptyCell: {
    paddingTop: 32,
    paddingBottom: 32,
  },
  autocompleteMinWidth: {
    minWidth: 120,
  },
  autocompleteWide: {
    minWidth: 200,
  },
}))

interface LabTestTableProps {
  rows: any[]
  newRow: any
  isRowAdded: boolean
  priceLoading: boolean
  externalText: string
  testNameOptions: any[]
  locationPathname: string
  handleModeChange: (option: any) => void
  handleOriginChange: (option: any) => void
  handleRequestedByChange: (option: any) => void
  setExternalText: (text: string) => void
  renderTestOrPackage: () => React.ReactNode
  renderParameters: (params: any) => React.ReactNode
  handleSaveRow: () => void
  handleDeleteRow: (index: number) => void
  getRequestedByOptions: () => any[]
}

const LabTestTable: React.FC<LabTestTableProps> = ({
  rows,
  newRow,
  isRowAdded,
  priceLoading,
  externalText,
  locationPathname,
  handleModeChange,
  handleOriginChange,
  handleRequestedByChange,
  setExternalText,
  renderTestOrPackage,
  renderParameters,
  handleSaveRow,
  handleDeleteRow,
  getRequestedByOptions,
}) => {
  const classes = useStyles()

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>
              {LmstableHeading?.Mode}
            </TableCell>
            {locationPathname.startsWith('/labordercreate') && (
              <TableCell className={classes.headerCell}>
                {LmstableHeading?.Origin}
              </TableCell>
            )}
            <TableCell className={classes.headerCellWide}>
              {LmstableHeading?.RequestedBy}
            </TableCell>
            <TableCell className={classes.headerCellExtraWide}>
              {LmstableHeading?.TestName}
            </TableCell>
            {newRow?.mode?.value === modes?.package && (
              <TableCell className={classes.headerCellWide}>
                Parameters
              </TableCell>
            )}
            <TableCell className={classes.headerCellSmall}>
              {LmstableHeading?.Price}
            </TableCell>
            <TableCell className={classes.headerCellAction}>
              {LmstableHeading?.Action}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* New Row Input */}
          <TableRow className={classes.newRow}>
            <TableCell>
              <Autocomplete
                value={newRow?.mode || null}
                onChange={(event, newValue) => handleModeChange(newValue)}
                options={[optionValue?.test, optionValue?.package]}
                disabled={isRowAdded}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) =>
                  option?.value === value?.value
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Select Mode'
                    size='small'
                  />
                )}
                className={classes.autocompleteMinWidth}
              />
            </TableCell>
            {locationPathname.startsWith('/labordercreate') && (
              <TableCell>
                <Autocomplete
                  value={newRow?.origin || null}
                  onChange={(event, newValue) => handleOriginChange(newValue)}
                  options={[
                    optionValue?.origin,
                    optionValue?.ipd,
                    optionValue?.b2b,
                    optionValue?.partner,
                    optionValue?.others,
                  ]}
                  getOptionLabel={(option) => option?.label || ''}
                  isOptionEqualToValue={(option, value) =>
                    option?.value === value?.value
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder='Select Origin'
                      size='small'
                    />
                  )}
                  className={classes.autocompleteMinWidth}
                />
              </TableCell>
            )}
            <TableCell>
              <Box>
                <Autocomplete
                  value={newRow?.requestedBy || null}
                  onChange={(event, newValue) =>
                    handleRequestedByChange(newValue)
                  }
                  options={getRequestedByOptions()}
                  getOptionLabel={(option) => option?.label || ''}
                  isOptionEqualToValue={(option, value) =>
                    option?.value === value?.value
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder='Select Requested By'
                      size='small'
                    />
                  )}
                  className={classes.autocompleteWide}
                />
                {newRow?.requestedBy?.value ===
                  optionValue?.external?.value && (
                  <input
                    type='text'
                    placeholder={externalName}
                    value={externalText}
                    onChange={(e) => setExternalText(e.target.value)}
                    className={classes.externalInput}
                  />
                )}
              </Box>
            </TableCell>
            <TableCell>{renderTestOrPackage()}</TableCell>
            {newRow?.mode?.value === modes?.package && (
              <TableCell>{renderParameters(newRow?.panels || [])}</TableCell>
            )}
            <TableCell>
              {priceLoading ? (
                <Skeleton width={60} height={30} />
              ) : (
                <Chip
                  label={`₹${newRow?.price || 0}`}
                  color='primary'
                  size='small'
                  className={classes.priceChip}
                />
              )}
            </TableCell>
            <TableCell>
              <IconButton
                onClick={handleSaveRow}
                color='primary'
                size='small'
                className={classes.addButton}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </TableCell>
          </TableRow>

          {/* Existing Rows */}
          {rows?.map((row: any, index) => (
            <TableRow key={index} className={classes.existingRow}>
              <TableCell>
                <Chip
                  label={row?.mode?.label ?? ''}
                  variant='outlined'
                  size='small'
                />
              </TableCell>
              {locationPathname?.startsWith('/labordercreate') && (
                <TableCell>
                  <Chip
                    label={row?.origin?.label ?? ''}
                    color='secondary'
                    size='small'
                  />
                </TableCell>
              )}
              <TableCell>
                <Typography variant='body2'>
                  {row?.requestedBy?.value ?? ''}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  {row?.mode?.value === modes?.package
                    ? row?.productName ?? ''
                    : row?.testName?.label ?? ''}
                </Typography>
              </TableCell>
              {row?.mode?.value === modes?.package && (
                <TableCell>
                  {row?.panels && row?.panels?.length > 0
                    ? renderParameters(row?.panels)
                    : row?.panel?.name ?? ''}
                </TableCell>
              )}
              <TableCell>
                <Chip
                  label={`₹${row?.panel?.price ?? 0}`}
                  color='success'
                  size='small'
                  className={classes.priceChip}
                />
              </TableCell>
              <TableCell>
                <IconButton
                  size='small'
                  onClick={() => handleDeleteRow(index)}
                  color='error'
                  className={classes.deleteButton}
                >
                  <DeleteOutlinedIcon fontSize='small' />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}

          {/* Empty State */}
          {rows?.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={
                  locationPathname?.startsWith('/labordercreate') ? 7 : 6
                }
                align='center'
                className={classes.emptyCell}
              >
                <Typography variant='body2' color='text.secondary'>
                  No tests added yet. Add a test using the form above.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default LabTestTable
